import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  getAdminPosts,
  createAdminPost,
  updateAdminPost,
  deleteAdminPost,
  AdminPost,
  uploadPostMedia,
  getAdminCategories,
} from "../../services/api/admin/blog";
import {
  getCategories,
  createCategory,
  updateCategory,
  deleteCategory,
  Category,
} from "../../services/api/admin/categories";
import {
  Plus,
  Edit2,
  Trash2,
  FileText,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  Globe,
  X,
  Save,
  Loader2,
  Upload,
  Image as ImageIcon,
} from "lucide-react";
import { BASE_URL } from "../../services/api/client";
import Swal from "sweetalert2";
import { RichTextEditor } from "./RichTextEditor";

export const BlogManager: React.FC = () => {
  const [posts, setPosts] = useState<AdminPost[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [categories, setCategories] = useState<{ id: string; name: string }[]>(
    [],
  );
  const [isSaving, setIsSaving] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  // Category Management
  const [activeTab, setActiveTab] = useState<"posts" | "categories">("posts");
  const [categoryList, setCategoryList] = useState<Category[]>([]);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ id: "", name: "" });

  const initialPostState: any = {
    title: "",
    content: "",
    status: "draft",
    seoTitle: "",
    seoDescription: "",
    seoKeywords: "",
    categoryIds: [],
    excerpt: "",
  };

  const [form, setForm] = useState<any>(initialPostState);

  const resolveMediaUrl = (value?: string | null) => {
    if (!value) return "";
    if (/^https?:\/\//i.test(value)) return value;
    return `${BASE_URL}${value.startsWith("/") ? value : `/${value}`}`;
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const [postsRes, catsRes, catListRes] = await Promise.all([
        getAdminPosts(),
        getAdminCategories(),
        getCategories(),
      ]);
      setPosts(postsRes.data);
      setCategories(catsRes.data);
      setCategoryList(catListRes);
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const calculateSeoScore = (
    post: any,
  ): { score: number; checks: { label: string; pass: boolean }[] } => {
    let score = 0;
    const checks = [];

    // Title check
    if (post.title?.length >= 10 && post.title?.length <= 60) {
      score += 20;
      checks.push({ label: "Panjang Judul Optimal", pass: true });
    } else {
      checks.push({ label: "Panjang Judul (10-60 karakter)", pass: false });
    }

    // SEO Description check
    if (
      post.seoDescription?.length >= 50 &&
      post.seoDescription?.length <= 160
    ) {
      score += 20;
      checks.push({ label: "Deskripsi Meta Optimal", pass: true });
    } else {
      checks.push({ label: "Deskripsi Meta (50-160 karakter)", pass: false });
    }

    // Content Length check
    const wordCount = (post.content || "").split(/\s+/).length;
    if (wordCount > 300) {
      score += 30;
      checks.push({ label: "Konten > 300 kata", pass: true });
    } else {
      checks.push({ label: "Konten minimal 300 kata", pass: false });
    }

    // Keywords check
    if (post.seoKeywords?.length > 0) {
      score += 15;
      checks.push({ label: "Kata Kunci Terisi", pass: true });
    } else {
      checks.push({ label: "Kata Kunci SEO", pass: false });
    }

    // SEO Title check
    if (post.seoTitle?.length > 0) {
      score += 15;
      checks.push({ label: "Judul SEO Terisi", pass: true });
    } else {
      checks.push({ label: "Judul SEO kustom", pass: false });
    }

    return { score, checks };
  };

  const seoAnalysis = useMemo(() => calculateSeoScore(form), [form]);

  const handleOpenEditor = (post?: AdminPost) => {
    setSelectedFile(null);
    setPreviewUrl(null);
    if (post) {
      setForm({
        id: post.id,
        title: post.title,
        content: post.content,
        status: post.status,
        slug: post.slug,
        seoTitle: post.seoTitle || "",
        seoDescription: post.seoDescription || "",
        seoKeywords: post.seoKeywords || "",
        categoryIds: post.categories?.map((c) => c.id) || [],
        excerpt: post.excerpt || "",
      });
      if (post.medias && post.medias.length > 0) {
        setPreviewUrl(resolveMediaUrl(post.medias[0].fileUrl));
      }
    } else {
      setForm({ ...initialPostState });
    }
    setIsEditorOpen(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      const postData = {
        title: form.title,
        content: form.content,
        status: form.status,
        seoTitle: form.seoTitle,
        seoDescription: form.seoDescription,
        seoKeywords: form.seoKeywords,
        categoryIds: form.categoryIds,
        excerpt: form.excerpt,
        tagIds: [],
      };

      let savedPost: AdminPost;
      if (form.id) {
        savedPost = await updateAdminPost(form.id, postData);
      } else {
        savedPost = await createAdminPost(postData);
      }

      if (selectedFile) {
        await uploadPostMedia(savedPost.id, selectedFile);
      }

      fetchData();
      setIsEditorOpen(false);
      Swal.fire({
        icon: "success",
        title: "Artikel Disimpan",
        text: "Artikel Anda telah berhasil diperbarui.",
        timer: 2000,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error("Failed to save post:", error);
      Swal.fire({
        icon: "error",
        title: "Gagal Menyimpan",
        text: "Terjadi kesalahan saat menyimpan artikel.",
        confirmButtonColor: "#0F172A",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    const result = await Swal.fire({
      title: "Hapus Artikel?",
      text: "Artikel yang dihapus tidak bisa dikembalikan!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      cancelButtonColor: "#64748B",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
      reverseButtons: true,
    });

    if (result.isConfirmed) {
      try {
        await deleteAdminPost(id);
        Swal.fire({
          icon: "success",
          title: "Terhapus!",
          text: "Artikel telah berhasil dihapus.",
          timer: 1500,
          showConfirmButton: false,
        });
        fetchData();
      } catch (error) {
        console.error("Failed to delete post:", error);
        Swal.fire({
          icon: "error",
          title: "Gagal Menghapus",
          text: "Terjadi kesalahan saat menghapus artikel.",
        });
      }
    }
  };

  const handleSaveCategory = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (categoryForm.id) {
        await updateCategory(categoryForm.id, categoryForm.name);
        Swal.fire({
          icon: "success",
          title: "Kategori Diperbarui",
          timer: 1500,
          showConfirmButton: false,
        });
      } else {
        await createCategory(categoryForm.name);
        Swal.fire({
          icon: "success",
          title: "Kategori Dibuat",
          timer: 1500,
          showConfirmButton: false,
        });
      }
      fetchData();
      setIsCategoryModalOpen(false);
      setCategoryForm({ id: "", name: "" });
    } catch (error) {
      Swal.fire({ icon: "error", title: "Gagal Menyimpan Kategori" });
    }
  };

  const handleDeleteCategory = async (id: string) => {
    const result = await Swal.fire({
      title: "Hapus Kategori?",
      text: "Seluruh artikel dalam kategori ini mungkin akan terpengaruh.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#EF4444",
      confirmButtonText: "Ya, Hapus!",
    });

    if (result.isConfirmed) {
      try {
        await deleteCategory(id);
        Swal.fire({
          icon: "success",
          title: "Terhapus",
          timer: 1500,
          showConfirmButton: false,
        });
        fetchData();
      } catch (error) {
        Swal.fire({ icon: "error", title: "Gagal Menghapus Kategori" });
      }
    }
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Blog & Artikel</h2>
          <p className="text-gray-500 text-sm">
            Kelola konten dan optimasi SEO.
          </p>
        </div>
        <div className="flex gap-4">
          <div className="bg-white p-1 rounded-xl border border-gray-200 flex">
            <button
              onClick={() => setActiveTab("posts")}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                activeTab === "posts"
                  ? "bg-slate-900 text-white shadow-lg"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              Artikel
            </button>
            <button
              onClick={() => setActiveTab("categories")}
              className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                activeTab === "categories"
                  ? "bg-slate-900 text-white shadow-lg"
                  : "text-gray-500 hover:bg-gray-50"
              }`}
            >
              Kategori
            </button>
          </div>
          {activeTab === "posts" ? (
            <button
              onClick={() => handleOpenEditor()}
              className="bg-slate-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-slate-800 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Tulis Artikel
            </button>
          ) : (
            <button
              onClick={() => {
                setCategoryForm({ id: "", name: "" });
                setIsCategoryModalOpen(true);
              }}
              className="bg-slate-900 text-white px-6 py-2 rounded-xl font-bold hover:bg-slate-800 transition-all flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> Kategori Baru
            </button>
          )}
        </div>
      </div>

      {activeTab === "posts" ? (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center p-20 text-gray-400">
              <Loader2 className="w-10 h-10 animate-spin mb-4 text-indigo-500" />
              <p className="font-bold font-mono uppercase tracking-widest text-xs">
                Synchronizing Articles...
              </p>
            </div>
          ) : (
            <table className="w-full text-sm text-left">
              <thead className="bg-gray-50 text-gray-500 font-bold border-b border-gray-200 uppercase text-[10px] tracking-widest">
                <tr>
                  <th className="px-6 py-4">Judul Artikel</th>
                  <th className="px-6 py-4">Penulis</th>
                  <th className="px-6 py-4 text-center">Status</th>
                  <th className="px-6 py-4 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {posts.map((post) => (
                  <tr
                    key={post.id}
                    className="hover:bg-gray-50 group transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-gray-100 flex-shrink-0 overflow-hidden border border-gray-100 shadow-sm transition-transform group-hover:scale-105">
                          {post.medias && post.medias.length > 0 ? (
                            <img
                              src={resolveMediaUrl(post.medias[0].fileUrl)}
                              className="w-full h-full object-cover"
                              alt="Cover"
                              loading="lazy"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-gray-300">
                              <ImageIcon className="w-6 h-6" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-bold text-gray-900 truncate max-w-md">
                            {post.title}
                          </div>
                          <div className="text-[10px] text-gray-400 font-mono mt-1 flex items-center gap-2">
                            {post.slug}
                            {post.categories && post.categories.length > 0 && (
                              <span className="bg-indigo-50 text-indigo-600 px-1.5 py-0.5 rounded text-[9px] font-black uppercase tracking-tighter">
                                {post.categories[0].name}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-indigo-100 flex items-center justify-center text-[10px] font-bold text-indigo-700">
                          {post.author?.fullName?.charAt(0) || "A"}
                        </div>
                        <span className="text-gray-600 text-xs font-medium">
                          {post.author?.fullName || "System"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <span
                        className={`text-[9px] font-black uppercase px-2 py-1 rounded-full shadow-sm border ${
                          post.status === "published"
                            ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                            : post.status === "archived"
                              ? "bg-gray-50 text-gray-500 border-gray-100"
                              : "bg-amber-50 text-amber-700 border-amber-100"
                        }`}
                      >
                        {post.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => handleOpenEditor(post)}
                          className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(post.id)}
                          className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
                {!isLoading && posts.length === 0 && (
                  <tr>
                    <td
                      colSpan={4}
                      className="px-6 py-20 text-center text-gray-400"
                    >
                      <FileText className="w-10 h-10 mx-auto mb-4 opacity-20" />
                      <p className="font-bold uppercase tracking-widest text-xs">
                        Belum ada artikel.
                      </p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden animate-slideUp">
          <table className="w-full text-sm text-left">
            <thead className="bg-gray-50 text-gray-500 font-bold border-b border-gray-200 uppercase text-[10px] tracking-widest">
              <tr>
                <th className="px-6 py-4">Nama Kategori</th>
                <th className="px-6 py-4">Slug</th>
                <th className="px-6 py-4 text-right">Aksi</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {categoryList.map((cat) => (
                <tr key={cat.id} className="hover:bg-gray-50 group">
                  <td className="px-6 py-4 font-bold text-gray-900">
                    {cat.name}
                  </td>
                  <td className="px-6 py-4 font-mono text-gray-500 text-xs">
                    {cat.slug}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setCategoryForm({ id: cat.id, name: cat.name });
                          setIsCategoryModalOpen(true);
                        }}
                        className="p-2 text-gray-400 hover:text-indigo-600 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteCategory(cat.id)}
                        className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {categoryList.length === 0 && (
                <tr>
                  <td
                    colSpan={3}
                    className="px-6 py-12 text-center text-gray-400 font-bold uppercase tracking-widest text-xs"
                  >
                    Belum ada kategori.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {isCategoryModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl p-8 w-full max-w-md animate-fadeIn">
            <h3 className="text-xl font-bold mb-6 text-gray-900">
              {categoryForm.id ? "Edit Kategori" : "Kategori Baru"}
            </h3>
            <form onSubmit={handleSaveCategory} className="space-y-6">
              <div>
                <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">
                  Nama Kategori
                </label>
                <input
                  autoFocus
                  type="text"
                  className="w-full border border-gray-300 rounded-xl p-3 focus:ring-2 focus:ring-slate-900 outline-none font-medium"
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  required
                />
              </div>
              <div className="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setIsCategoryModalOpen(false)}
                  className="px-4 py-2 text-gray-500 font-bold text-xs uppercase hover:bg-gray-100 rounded-lg"
                >
                  Batal
                </button>
                <button
                  type="submit"
                  className="px-6 py-2 bg-slate-900 text-white font-bold text-xs uppercase rounded-lg hover:bg-black"
                >
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {isEditorOpen && (
        <div className="fixed inset-0 z-50 bg-gray-100 flex flex-col animate-slideUp">
          <div className="bg-white border-b border-gray-200 px-6 py-4 flex justify-between items-center shadow-sm">
            <button
              onClick={() => setIsEditorOpen(false)}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
            <div className="flex gap-4 items-center">
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className="bg-transparent border-none text-xs font-black uppercase focus:ring-0 cursor-pointer text-gray-600"
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="px-6 py-2 bg-slate-900 text-white font-bold rounded-xl shadow-lg flex items-center gap-2 hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? "Saving..." : "Save Article"}
              </button>
            </div>
          </div>
          <div className="flex-1 flex overflow-hidden">
            <div className="flex-1 overflow-y-auto p-4 md:p-10 bg-gray-50">
              <div className="max-w-4xl mx-auto space-y-8">
                <div className="bg-white p-8 md:p-12 rounded-[2.5rem] shadow-sm border border-gray-200">
                  <input
                    type="text"
                    className="w-full text-3xl md:text-5xl font-black border-none py-4 bg-white text-gray-900 focus:ring-0 outline-none placeholder:text-gray-100"
                    placeholder="Article Title"
                    value={form.title}
                    onChange={(e) =>
                      setForm({ ...form, title: e.target.value })
                    }
                  />

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-6 mb-10">
                    <div className="space-y-6">
                      <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">
                          Category
                        </label>
                        <select
                          className="w-full border border-gray-100 rounded-2xl px-5 py-3.5 bg-gray-50 text-gray-900 text-sm focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none"
                          value={form.categoryIds?.[0] || ""}
                          onChange={(e) =>
                            setForm({ ...form, categoryIds: [e.target.value] })
                          }
                        >
                          <option value="">Select Category</option>
                          {categories.map((cat) => (
                            <option key={cat.id} value={cat.id}>
                              {cat.name}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">
                          Short Excerpt
                        </label>
                        <textarea
                          rows={4}
                          className="w-full border border-gray-100 rounded-2xl px-5 py-4 bg-gray-50 text-gray-900 text-sm focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none resize-none leading-relaxed"
                          placeholder="Brief overview of the article..."
                          value={form.excerpt}
                          onChange={(e) =>
                            setForm({ ...form, excerpt: e.target.value })
                          }
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] mb-3">
                        Article Cover
                      </label>
                      <div
                        onClick={() => fileInputRef.current?.click()}
                        className="relative aspect-[4/3] rounded-[2rem] border-2 border-dashed border-gray-200 bg-gray-50 overflow-hidden cursor-pointer hover:border-indigo-400 hover:bg-white transition-all group shadow-inner"
                      >
                        {previewUrl ? (
                          <>
                            <img
                              src={previewUrl}
                              className="w-full h-full object-cover"
                              alt="Preview"
                              loading="lazy"
                            />
                            <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center text-white font-black text-[10px] uppercase tracking-widest gap-2 backdrop-blur-[2px]">
                              <Upload className="w-4 h-4" /> Change Cover
                            </div>
                          </>
                        ) : (
                          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-300 gap-4">
                            <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center shadow-sm">
                              <Upload className="w-6 h-6 text-indigo-500" />
                            </div>
                            <span className="text-[10px] font-black uppercase tracking-widest">
                              Upload Cover
                            </span>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          className="hidden"
                          accept="image/*"
                          onChange={handleFileChange}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-gray-50 pt-8">
                    <RichTextEditor
                      content={form.content}
                      onChange={(content) => setForm({ ...form, content })}
                      placeholder="Tulis konten artikel di sini..."
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="w-96 bg-white border-l border-gray-200 p-8 space-y-8 overflow-y-auto">
              <div className="bg-indigo-50/50 p-6 rounded-[2rem] border border-indigo-100/50">
                <h4 className="font-black text-indigo-900 uppercase text-[10px] tracking-[0.2em] mb-6 flex items-center gap-2">
                  <TrendingUp className="w-4 h-4" /> SEO Analysis
                </h4>
                <div className="space-y-4">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-[10px] font-black text-gray-400 tracking-wider">
                      HEALTH SCORE
                    </span>
                    <span
                      className={`text-lg font-black ${
                        seoAnalysis.score > 70
                          ? "text-emerald-500"
                          : seoAnalysis.score > 40
                            ? "text-amber-500"
                            : "text-red-500"
                      }`}
                    >
                      {seoAnalysis.score}
                    </span>
                  </div>
                  <div className="w-full bg-white h-2 rounded-full overflow-hidden shadow-inner">
                    <div
                      className={`h-full rounded-full transition-all duration-700 ${
                        seoAnalysis.score > 70
                          ? "bg-emerald-500"
                          : seoAnalysis.score > 40
                            ? "bg-amber-500"
                            : "bg-red-500"
                      }`}
                      style={{ width: `${seoAnalysis.score}%` }}
                    ></div>
                  </div>
                  <div className="pt-4 space-y-3">
                    {seoAnalysis.checks.map((c, i) => (
                      <div key={i} className="flex items-center gap-3">
                        {c.pass ? (
                          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                        ) : (
                          <div className="w-1.5 h-1.5 rounded-full border border-gray-300" />
                        )}
                        <span
                          className={`text-[10px] font-bold ${
                            c.pass ? "text-gray-700" : "text-gray-300 italic"
                          }`}
                        >
                          {c.label}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-6 pt-2">
                <h4 className="font-black text-gray-900 uppercase text-[10px] tracking-[0.2em] flex items-center gap-2">
                  <Globe className="w-4 h-4" /> SEO Metadata
                </h4>
                <div className="space-y-6">
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Meta Title
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 bg-gray-50 text-gray-900 text-sm focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none"
                      placeholder="Specific title for Google..."
                      value={form.seoTitle}
                      onChange={(e) =>
                        setForm({ ...form, seoTitle: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Meta Description
                    </label>
                    <textarea
                      rows={5}
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 bg-gray-50 text-gray-900 text-sm focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none resize-none leading-relaxed"
                      placeholder="Description for search engines..."
                      value={form.seoDescription}
                      onChange={(e) =>
                        setForm({ ...form, seoDescription: e.target.value })
                      }
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-gray-400 uppercase tracking-widest mb-2">
                      Meta Keywords
                    </label>
                    <input
                      type="text"
                      className="w-full border border-gray-100 rounded-xl px-4 py-3 bg-gray-50 text-gray-900 text-sm focus:bg-white focus:ring-4 focus:ring-indigo-500/10 transition-all outline-none"
                      placeholder="e.g. promo, surabaya, event"
                      value={form.seoKeywords}
                      onChange={(e) =>
                        setForm({ ...form, seoKeywords: e.target.value })
                      }
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
